import React, { useEffect } from 'react';
import { DatePicker, Form, Input, Modal, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { REACH_OUT_ACTIVITY_TYPE_LABELS } from '../../leads/constants';
import type { ReachOutActivityType } from '../../leads/types';
import type { ResolveFollowupPayload } from '../api/tasks';
import ScheduleNextFollowupFields from './ScheduleNextFollowupFields';

export interface ResolveFollowupFormValues {
  activityType: ReachOutActivityType;
  interactionAt: Dayjs;
  note: string;
  scheduleNext: boolean;
  nextDueDate?: Dayjs;
  nextTitle?: string;
  nextDescription?: string;
}

interface ResolveFollowupModalProps {
  open: boolean;
  taskId: string | null;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (taskId: string, payload: ResolveFollowupPayload) => void;
}

const activityOptions = Object.entries(REACH_OUT_ACTIVITY_TYPE_LABELS).map(([value, label]) => ({
  value: Number(value) as ReachOutActivityType,
  label,
}));

const ResolveFollowupModal: React.FC<ResolveFollowupModalProps> = ({
  open,
  taskId,
  loading,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<ResolveFollowupFormValues>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        interactionAt: dayjs(),
        scheduleNext: false,
      });
    } else {
      form.resetFields();
    }
  }, [open, form]);

  const handleOk = () => {
    if (!taskId) return;
    form.validateFields().then((values) => {
      onSubmit(taskId, {
        activityType: values.activityType,
        interactionAt: values.interactionAt.toISOString(),
        note: values.note,
        scheduleNext: values.scheduleNext,
        nextDueDate:
          values.scheduleNext && values.nextDueDate
            ? values.nextDueDate.format('YYYY-MM-DD')
            : undefined,
        nextTitle: values.scheduleNext ? values.nextTitle : undefined,
        nextDescription: values.scheduleNext ? values.nextDescription : undefined,
      });
    });
  };

  return (
    <Modal
      title="Mark as completed"
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      okText="Mark as completed"
      destroyOnClose
      width={520}
    >
      <div className="cp-form-section">
        <Form form={form} layout="vertical" initialValues={{ scheduleNext: false }}>
        <Form.Item
          name="activityType"
          label="How did you reach out?"
          rules={[{ required: true, message: 'Select a channel' }]}
        >
          <Select placeholder="Select channel" options={activityOptions} />
        </Form.Item>
        <Form.Item
          name="interactionAt"
          label="When"
          rules={[{ required: true, message: 'Interaction time is required' }]}
        >
          <DatePicker
            showTime
            style={{ width: '100%' }}
            disabledDate={(current) => current != null && current > dayjs().endOf('day')}
            getPopupContainer={(trigger) => trigger.parentElement!}
            disabledTime={(current) => {
              if (current == null || !current.isSame(dayjs(), 'day')) {
                return {};
              }
              const now = dayjs();
              return {
                disabledHours: () =>
                  Array.from({ length: 24 }, (_, h) => h).filter((h) => h > now.hour()),
                disabledMinutes: (hour: number) =>
                  hour === now.hour()
                    ? Array.from({ length: 60 }, (_, m) => m).filter((m) => m > now.minute())
                    : [],
                disabledSeconds: (hour: number, minute: number) =>
                  hour === now.hour() && minute === now.minute()
                    ? Array.from({ length: 60 }, (_, s) => s).filter((s) => s > now.second())
                    : [],
              };
            }}
          />
        </Form.Item>
        <Form.Item
          name="note"
          label="What happened"
          rules={[{ required: true, message: 'Note is required' }]}
        >
          <Input.TextArea rows={3} placeholder="Describe the interaction" maxLength={4000} />
        </Form.Item>
        <ScheduleNextFollowupFields />
        </Form>
      </div>
    </Modal>
  );
};

export default ResolveFollowupModal;
